import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EstimateResult } from '../App';

interface QuoteCardProps {
  estimate: EstimateResult;
  onReset: () => void;
  imageFile: File | null;
}

const QuoteCard: React.FC<QuoteCardProps> = ({ estimate, onReset, imageFile }) => {
  const [copied, setCopied] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const trackEstimatorEvent = (eventName: string, params: Record<string, unknown> = {}) => {
    if (typeof window === 'undefined') return;
    const tracker = (window as any).trackPalmetteauxEvent;
    if (typeof tracker === 'function') {
      tracker(eventName, { location: 'estimator_result', ...params });
      return;
    }
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: eventName,
      location: 'estimator_result',
      ...params
    });
  };

  const statusMeta: Record<
    'ready_to_send' | 'needs_review' | 'info_missing',
    { label: string; pill: string; accent: string; icon: JSX.Element }
  > = {
    ready_to_send: {
      label: 'Ready to Send',
      pill: 'bg-green-50 border-green-200 text-green-700',
      accent: 'text-green-700',
      icon: (
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      )
    },
    needs_review: {
      label: 'Needs Review',
      pill: 'bg-amber-50 border-amber-200 text-amber-700',
      accent: 'text-amber-700',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.66 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.34.19 3 .73 3z"
        />
      )
    },
    info_missing: {
      label: 'Info Missing',
      pill: 'bg-red-50 border-red-200 text-red-700',
      accent: 'text-red-700',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.66 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.34.19 3 .73 3z"
        />
      )
    }
  };

  const formatEstimateText = (includeImageNudge: boolean = false) => {
    const customerNotes = [...(estimate.standardNotes || []), ...(estimate.notes || [])];
    const text = `
Palmetteaux Services Estimate
Date: ${new Date().toLocaleDateString()}

Estimated Price Range (Labor + Materials):
$${estimate.priceRange.min} - $${estimate.priceRange.max}

Time Estimate: ${estimate.timeEstimate}
Visits Required: ${estimate.numberOfVisits}

What to Expect & Prepare:
${estimate.customerExpectations}

${estimate.customerMaterials.length > 0 ? `Customer-supplied items:\n${estimate.customerMaterials.map((m) => `- ${m}`).join('\n')}\n` : ''}
Notes & Conditions:
${customerNotes.map((n) => `• ${n}`).join('\n')}

*This is an AI-generated draft. Chris will review and confirm the final price before booking.*`.trim();

    if (includeImageNudge) {
      return text + '\n\nPlease attach the photo(s) of the area when you send this message.';
    }

    return text;
  };

  const handleCopy = () => {
    const text = formatEstimateText(false);

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      trackEstimatorEvent('estimator_copy_result', { status: estimate.status });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    const messageBody = formatEstimateText(true);
    trackEstimatorEvent('estimator_sms_result', {
      status: estimate.status,
      has_image: Boolean(imageFile)
    });
    window.location.href = `sms:+18646250942?body=${encodeURIComponent(messageBody)}`;
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col gap-3 border-b border-highlight/60 pb-6"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-display font-bold text-charcoal">Quote Review</h2>
            <p className="text-sm text-charcoal/60 mt-1">{new Date().toLocaleDateString()}</p>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${statusMeta[estimate.status].pill}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
              stroke="currentColor"
            >
              {statusMeta[estimate.status].icon}
            </svg>
            {statusMeta[estimate.status].label}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-charcoal/70">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-highlight/50 text-charcoal font-semibold uppercase tracking-wide text-[11px]">
            AI Draft · Human Reviewed
          </span>
          <span className={`font-semibold ${statusMeta[estimate.status].accent}`}>
            Confidence: {estimate.confidenceScore}
          </span>
          {estimate.estimatedReviewTime && (
            <span className="text-charcoal/60">
              Est. review time: {estimate.estimatedReviewTime}
            </span>
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-brand-50/50 rounded-xl p-4 border border-brand-100"
      >
        <div className="text-sm font-medium text-brand-600 uppercase tracking-wider">
          Estimated Cost (AI ballpark)
        </div>
        <div className="text-3xl font-bold text-brand-700 mt-1">
          ${estimate.priceRange.min} - ${estimate.priceRange.max}
        </div>
        <div className="text-xs text-brand-800/60 mt-2 italic">
          *AI starting range. I verify this against your photos and the minimum visit fee
          (Greenville $79 / extended $99, credited if you proceed) and flag hidden damage or access
          issues before scheduling.*
        </div>
      </motion.div>

      {estimate.confidenceScore === 'low' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex gap-3"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-yellow-600 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="font-semibold text-yellow-800">Low Confidence Estimate</h4>
            <p className="text-sm text-yellow-700 mt-1">
              {estimate.confidenceReason || 'The image might be unclear or the damage is complex.'}
            </p>
          </div>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-charcoal/60 uppercase tracking-wider mb-2">
              What to Expect & Prepare
            </h3>
            <p className="text-charcoal/90 leading-relaxed">{estimate.customerExpectations}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-charcoal/60 uppercase tracking-wider mb-2">
              Time & Visits
            </h3>
            <div className="space-y-1">
              <p className="text-charcoal/90">
                <span className="font-medium">Duration:</span> {estimate.timeEstimate}
              </p>
              <p className="text-charcoal/90">
                <span className="font-medium">Visits:</span> {estimate.numberOfVisits}
              </p>
            </div>
          </div>
          {estimate.customerMaterials.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-charcoal/60 uppercase tracking-wider mb-2">
                Customer-Supplied Items
              </h3>
              <ul className="space-y-1">
                {estimate.customerMaterials.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-charcoal/80">
                    <span className="mt-1.5 w-1.5 h-1.5 bg-brand-400 rounded-full flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}>
        <div className="space-y-2 rounded-xl border border-highlight/60 p-4 bg-highlight/20">
          <button
            type="button"
            onClick={() => setShowNotes((prev) => !prev)}
            className="w-full flex items-center justify-between text-left"
          >
            <div>
              <h3 className="text-sm font-semibold text-charcoal/60 uppercase tracking-wider">
                Notes & Conditions
              </h3>
              {!showNotes && (
                <p className="text-xs text-charcoal/60 mt-1">Tap to view scope caveats</p>
              )}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-charcoal/60 transition-transform ${
                showNotes ? 'rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showNotes && (
            <ul className="space-y-2 text-charcoal/80 pt-2">
              {[...(estimate.standardNotes || []), ...(estimate.notes || [])].map((note, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-charcoal/40" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>

      <div className="pt-6 border-t border-highlight/60 flex flex-col gap-4">
        <div className="text-sm text-charcoal/70">
          I’ll review this AI draft and confirm the final number (and any minimum-visit adjustments)
          before we book.
        </div>
        <button
          onClick={handleShare}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-500 transition-colors shadow-lg shadow-brand-600/20 cta-glow estimator-cta"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          Send to Chris for Final Review
        </button>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-charcoal/5 text-charcoal font-semibold rounded-xl hover:bg-charcoal/10 transition-colors"
          >
            {copied ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                  />
                </svg>
                Copy Estimate
              </>
            )}
          </button>
          <button
            onClick={onReset}
            className="flex-1 px-6 py-3 text-charcoal/70 font-semibold rounded-xl border border-highlight hover:bg-highlight/40 transition-colors"
          >
            Regenerate Quote
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;
